import { create } from "zustand";

interface StoredAuth {
  token: string | null;
  expiresAt: string | null;
  username: string | null;
  roles: string[];
}

type LogoutReason = "manual" | "unauthorized" | "expired";
type AuthProvider = "local" | "keycloak";

interface AuthState {
  token: string | null;
  expiresAt: string | null;
  username: string | null;
  roles: string[];
  authRequired: boolean | null;
  provider: AuthProvider;
  authLoading: boolean;
  oidcIssuer: string | null;
  oidcClientId: string | null;
  lastLogoutReason: LogoutReason | null;
  logoutVersion: number;
  setAuth: (token: string, expiresAt: string | null, username: string | null, roles?: string[]) => void;
  canManageSettings: () => boolean;
  canManageS3Tiering: () => boolean;
  isLegalRestrictedUser: () => boolean;
  canAccessPath: (path: string) => boolean;
  clearAuth: (reason?: LogoutReason) => void;
  setAuthRequired: (required: boolean) => void;
  setProvider: (provider: AuthProvider) => void;
  setAuthLoading: (loading: boolean) => void;
  setOidcConfig: (issuer: string | null, clientId: string | null) => void;
  isAuthenticated: () => boolean;
}

const STORAGE_KEY = "pec-auth-state";

function readStoredAuth(): StoredAuth {
  if (typeof window === "undefined") {
    return { token: null, expiresAt: null, username: null, roles: [] };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { token: null, expiresAt: null, username: null, roles: [] };
    }
    const parsed = JSON.parse(raw) as StoredAuth;
    return {
      token: parsed?.token ?? null,
      expiresAt: parsed?.expiresAt ?? null,
      username: parsed?.username ?? null,
      roles: Array.isArray(parsed?.roles) ? parsed.roles.map((r) => String(r)) : []
    };
  } catch (error) {
    console.warn("Failed to read stored auth state", error);
    return { token: null, expiresAt: null, username: null, roles: [] };
  }
}

function persistAuth(state: StoredAuth) {
  if (typeof window === "undefined") {
    return;
  }
  if (!state.token) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn("Failed to persist auth state", error);
  }
}

function isTokenValid(expiresAt: string | null): boolean {
  if (!expiresAt) {
    return true;
  }
  const expiry = Date.parse(expiresAt);
  if (Number.isNaN(expiry)) {
    return false;
  }
  return expiry > Date.now();
}

const stored = readStoredAuth();
const LEGAL_ALLOWED_PATHS = new Set(["/archive", "/search", "/profile"]);

function normalizeRoles(roles: string[]): Set<string> {
  return new Set(
    (roles || [])
      .map((r) => String(r).trim().toLowerCase().replace(/_/g, "-"))
      .filter((r) => r.length > 0)
  );
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: stored.token,
  expiresAt: stored.expiresAt,
  username: stored.username,
  roles: stored.roles,
  authRequired: null,
  provider: "local",
  authLoading: false,
  oidcIssuer: null,
  oidcClientId: null,
  lastLogoutReason: null,
  logoutVersion: 0,
  setAuth: (token, expiresAt, username, roles = []) => {
    persistAuth({ token, expiresAt, username, roles });
    set({
      token,
      expiresAt,
      username,
      roles,
      lastLogoutReason: null
    });
  },
  canManageSettings: () => {
    const state = get();
    if (state.provider !== "keycloak") {
      return true;
    }
    const roles = normalizeRoles(state.roles || []);
    return roles.has("pec-admin") || roles.has("realm-admin") || roles.has("admin");
  },
  canManageS3Tiering: () => {
    const state = get();
    if (state.provider !== "keycloak") {
      return true;
    }
    const roles = normalizeRoles(state.roles || []);
    return roles.has("pec-admin") || roles.has("realm-admin") || roles.has("admin");
  },
  isLegalRestrictedUser: () => {
    const state = get();
    if (state.provider !== "keycloak") {
      return false;
    }
    const roles = normalizeRoles(state.roles || []);
    return roles.has("pec-legal") || roles.has("realm-legal") || roles.has("legal");
  },
  canAccessPath: (path) => {
    const state = get();
    if (!state.isLegalRestrictedUser()) {
      return true;
    }

    if (path === "/") {
      return false;
    }

    for (const allowedPath of LEGAL_ALLOWED_PATHS) {
      if (path === allowedPath || path.startsWith(`${allowedPath}/`)) {
        return true;
      }
    }
    return false;
  },
  clearAuth: (reason = "manual") => {
    persistAuth({ token: null, expiresAt: null, username: null, roles: [] });
    set((state) => ({
      token: null,
      expiresAt: null,
      username: null,
      roles: [],
      authLoading: false,
      lastLogoutReason: reason,
      logoutVersion: state.logoutVersion + 1
    }));
  },
  setAuthRequired: (required) => {
    set({ authRequired: required });
  },
  setProvider: (provider) => {
    set({ provider });
  },
  setAuthLoading: (loading) => {
    set({ authLoading: loading });
  },
  setOidcConfig: (issuer, clientId) => {
    set({ oidcIssuer: issuer, oidcClientId: clientId });
  },
  isAuthenticated: () => {
    const state = get();
    if (state.authRequired === false) {
      return true;
    }
    if (!state.token) {
      return false;
    }
    return isTokenValid(state.expiresAt);
  }
}));
